import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Package, Send, Check } from "lucide-react";
import { api } from "../lib/api";
import { runPosterPack, downloadZipFromUrl } from "../lib/posterPack";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") || "";

// Bouton réutilisable : génère un pack multi-formats, affiche un APERÇU (vignettes)
// AVANT le téléchargement du ZIP, et permet de PUBLIER les visuels vers le
// Marketing Studio (Facebook + Instagram, en « À approuver »).
export function PosterPackButton({
  endpoint, getBody, filename = "pack-affiches.zip", label = "Pack (aperçu + ZIP)",
  discipline, getCaption, testId = "poster-pack-btn", className, disabled,
}) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  const [caption, setCaption] = useState("");
  const [selected, setSelected] = useState({}); // {format: bool}
  const [publishing, setPublishing] = useState(false);

  const start = async () => {
    setBusy(true);
    toast.info("📦 Création du pack… ~1-2 min");
    try {
      const body = typeof getBody === "function" ? getBody() : (getBody || {});
      const res = await runPosterPack({ api, endpoint, body });
      setResult(res);
      setCaption(res.caption || (typeof getCaption === "function" ? getCaption() : "") || "");
      // Sélection par défaut pour publication : carré + story (idéaux pour le feed)
      const sel = {};
      (res.formats || []).forEach((f) => {
        sel[f.format] = f.format === "carre" || f.format === "story";
      });
      if (!Object.values(sel).some(Boolean)) {
        (res.formats || []).forEach((f) => { sel[f.format] = true; });
      }
      setSelected(sel);
      setOpen(true);
      toast.success("📦 Pack prêt ! Aperçu ci-dessous.");
    } catch (e) {
      toast.error(e?.message || "Échec du pack. Réessaie.");
    } finally { setBusy(false); }
  };

  const doDownload = async () => {
    try {
      await downloadZipFromUrl({ backendUrl: BACKEND, zipUrl: result.zip_url, filename });
      toast.success("📦 ZIP téléchargé !");
    } catch { toast.error("Téléchargement impossible."); }
  };

  const doPublish = async () => {
    const chosen = (result.formats || []).filter((f) => selected[f.format]);
    if (chosen.length === 0) { toast.error("Sélectionne au moins un visuel."); return; }
    setPublishing(true);
    try {
      const { data } = await api.post("/poster/pack/publish", {
        image_urls: chosen.map((f) => f.full_url || f.url),
        caption,
        discipline: discipline || undefined,
      });
      toast.success(data.message || "Envoyé au Marketing Studio (à approuver).");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi au Studio marketing.");
    } finally { setPublishing(false); }
  };

  const toggle = (fmt) => setSelected((s) => ({ ...s, [fmt]: !s[fmt] }));

  return (
    <>
      <button
        onClick={start}
        disabled={busy || disabled}
        data-testid={testId}
        className={className || "inline-flex items-center gap-2 border border-rose-500/50 text-rose-200 hover:bg-rose-500/10 disabled:opacity-60 px-4 py-2.5 rounded-lg text-sm font-semibold"}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
        {busy ? "Création du pack…" : label}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="poster-pack-preview">
          <DialogHeader>
            <DialogTitle>📦 Aperçu du pack — {result?.formats?.length || 0} format(s)</DialogTitle>
            <DialogDescription>
              Vérifie les visuels, télécharge le ZIP ou publie-les vers le Marketing Studio (à approuver).
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="poster-pack-thumbs">
            {(result?.formats || []).map((f) => (
              <button
                key={f.format}
                type="button"
                onClick={() => toggle(f.format)}
                data-testid={`pack-thumb-${f.format}`}
                className={`relative rounded-lg overflow-hidden border-2 transition ${
                  selected[f.format] ? "border-rose-500 ring-2 ring-rose-500/30" : "border-zinc-700 hover:border-zinc-500"
                }`}
              >
                <img
                  src={f.full_url || f.url}
                  alt={f.label}
                  className="w-full h-40 object-contain bg-zinc-900"
                  loading="lazy"
                />
                <span className="block text-[11px] px-2 py-1 bg-zinc-900 text-zinc-300 truncate">{f.label}</span>
                {selected[f.format] && (
                  <span className="absolute top-1.5 right-1.5 bg-rose-600 text-white rounded-full p-0.5">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="mt-2">
            <div className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
              Légende (éditable) — utilisée à la publication
            </div>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={4}
              data-testid="pack-publish-caption"
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100"
              placeholder="Écris une légende pour Facebook / Instagram…"
            />
            <p className="text-[11px] text-zinc-500 mt-1">
              🖼️ Coche les visuels à publier (carré + story recommandés pour le feed).
            </p>
          </div>

          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <button
              onClick={doDownload}
              data-testid="pack-download-zip-btn"
              className="inline-flex items-center justify-center gap-2 border border-zinc-600 text-zinc-200 hover:bg-zinc-700 px-4 py-2.5 rounded-lg text-sm font-semibold"
            >
              <Download className="w-4 h-4" /> Télécharger le ZIP
            </button>
            <button
              onClick={doPublish}
              disabled={publishing}
              data-testid="pack-publish-btn"
              className="inline-flex items-center justify-center gap-2 bg-rose-600 hover:bg-rose-500 disabled:opacity-60 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {publishing ? "Envoi…" : "Publier au Studio marketing (FB + IG)"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default PosterPackButton;
